import {
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
} from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardEdit,
  HandHeart,
  HeartHandshake,
  Hospital,
  PawPrint,
  Play,
  Sparkles,
  Store,
  UtensilsCrossed,
  type LucideProps,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@project/user/components/motion";
import { Button } from "@project/components/ui/button";
import { useI18n } from "@project/lib/use-i18n";
import logo from "@project/assets/jivandhara-logo.png";
import heroBg from "@project/assets/hero-vrudhashram-courtyard.webp";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@project/components/ui/dialog";

const STORY_VIDEO_URL =
  "https://s3.in-west2.purestore.io/jivandhara-prod/jivandhara_video/Jivandhara_final_video.mp4";

type Side = "left" | "right";

type Service = {
  icon: ComponentType<LucideProps>;
  titleKey: string;
  to: string;
  side: Side;
  gradient: string;
  accent: string;
};

// Ordered row by row: [left, right] for each of the 4 rows.
const services: Service[] = [
  {
    icon: Hospital,
    titleKey: "home.hero.services.medicalCentre",
    to: "/healthcare",
    side: "left",
    gradient: "linear-gradient(135deg, #F7923F 0%, #E2521C 100%)",
    accent: "#E2521C",
  },
  {
    icon: TiffinIcon,
    titleKey: "home.hero.services.tiffin",
    to: "/tiffin-seva",
    side: "right",
    gradient: "linear-gradient(225deg, #DE4A93 0%, #A8195B 100%)",
    accent: "#B81F66",
  },
  {
    icon: PawPrint,
    titleKey: "home.hero.services.gaushala",
    to: "/gaushala",
    side: "left",
    gradient: "linear-gradient(135deg, #6A4FC4 0%, #3F2A88 100%)",
    accent: "#4A3399",
  },
  {
    icon: UtensilsCrossed,
    titleKey: "home.hero.services.bhojanshala",
    to: "/bhojanshala",
    side: "right",
    gradient: "linear-gradient(225deg, #2BC0CC 0%, #0A8396 100%)",
    accent: "#0A8396",
  },
  {
    icon: Store,
    titleKey: "home.hero.services.mahilaUtkarsh",
    to: "/gruh-udhyog",
    side: "left",
    gradient: "linear-gradient(135deg, #86C552 0%, #4A932B 100%)",
    accent: "#4A932B",
  },
  {
    icon: HandHeart,
    titleKey: "home.hero.services.donation",
    to: "/donate",
    side: "right",
    gradient: "linear-gradient(225deg, #F7BD2E 0%, #E08E0B 100%)",
    accent: "#D98508",
  },
  {
    icon: Building2,
    titleKey: "home.hero.services.csr",
    to: "/csr-partnership",
    side: "left",
    gradient: "linear-gradient(135deg, #8E9C3C 0%, #5C6A21 100%)",
    accent: "#5C6A21",
  },
  {
    icon: HeartHandshake,
    titleKey: "home.hero.services.elderCare",
    to: "/elder-care-program",
    side: "right",
    gradient: "linear-gradient(225deg, #2E95DA 0%, #145FA4 100%)",
    accent: "#145FA4",
  },
];

/*
 * The flower is drawn on a fixed 604 x 592 canvas and scaled to fit, so the
 * petal outlines keep their exact proportions at every width.
 *
 * The four left-hand petals are traced individually (each has its own
 * silhouette); the right-hand petals mirror them across the vertical axis.
 * Every petal tucks its inner edge under the centre circle.
 */
const FLOWER_W = 604;
const FLOWER_H = 592;
const CENTER = { x: 302, y: 295 };
const CENTER_SIZE = 176;

type Pt = [number, number];
type Seg = ["M" | "L", Pt] | ["Q", Pt, Pt] | ["C", Pt, Pt, Pt] | ["Z"];

// Top row: small outer corner, wide sweeping inner corner, and a stem that
// curves down into the centre.
const OUTER_PETAL: Seg[] = [
  ["M", [95, 52]],
  ["L", [217, 52]],
  ["C", [259, 52], [293, 86], [293, 128]],
  ["L", [293, 222]],
  ["L", [280, 222]],
  ["C", [282, 192], [262, 166], [232, 166]],
  ["L", [113, 166]],
  ["C", [96.4, 166], [83, 152.6], [83, 136]],
  ["L", [83, 64]],
  ["Q", [83, 52], [95, 52]],
  ["Z"],
];

// Second row: gently sloping top that rolls into a large curve toward the
// centre, with a soft rounded bottom-outer corner.
const UPPER_MIDDLE_PETAL: Seg[] = [
  ["M", [43, 176]],
  ["C", [100, 176], [135, 179], [160, 182]],
  ["C", [205, 190], [238, 222], [240, 264]],
  ["L", [240, 291]],
  ["L", [78, 291]],
  ["C", [53, 291], [33, 271], [33, 246]],
  ["L", [33, 186]],
  ["Q", [33, 176], [43, 176]],
  ["Z"],
];

// Third row: sits slightly further out, with a broad curve on its
// bottom-inner corner.
const LOWER_MIDDLE_PETAL: Seg[] = [
  ["M", [30, 301]],
  ["L", [205, 301]],
  ["C", [222, 301], [236, 312], [240, 328]],
  ["L", [240, 352]],
  ["C", [240, 385], [215, 410], [185, 410]],
  ["L", [46, 410]],
  ["C", [30.5, 410], [18, 397.5], [18, 382]],
  ["L", [18, 313]],
  ["Q", [18, 301], [30, 301]],
  ["Z"],
];

// Row order top to bottom; the bottom row is the top row flipped vertically.
const ROW_SHAPES: { shape: Seg[]; flipY: boolean }[] = [
  { shape: OUTER_PETAL, flipY: false },
  { shape: UPPER_MIDDLE_PETAL, flipY: false },
  { shape: LOWER_MIDDLE_PETAL, flipY: false },
  { shape: OUTER_PETAL, flipY: true },
];

function petalPath(row: number, flipX: boolean) {
  const { shape, flipY } = ROW_SHAPES[row];
  const p = ([x, y]: Pt) =>
    `${flipX ? FLOWER_W - x : x} ${flipY ? 2 * CENTER.y - y : y}`;
  return shape.map(([cmd, ...pts]) => [cmd, ...pts.map(p)].join(" ")).join(" ");
}

// Icon + title placement for the left-hand petals, per row: both are
// centred on `cx` (the middle of the petal's visible body). The right-hand
// petals mirror `cx`.
const LEFT_LAYOUT: {
  cx: number;
  iconTop: number;
  iconSize: number;
  textW: number;
}[] = [
  { cx: 184, iconTop: 58, iconSize: 48, textW: 164 },
  { cx: 128, iconTop: 184, iconSize: 50, textW: 124 },
  { cx: 122, iconTop: 306, iconSize: 46, textW: 178 },
  { cx: 184, iconTop: 436, iconSize: 52, textW: 160 },
];
const ICON_TEXT_GAP = 8;
const RING_RADIUS = 285;
// Petals bloom clockwise from the top-left (indices into `services`).
const BLOOM_ORDER = [0, 1, 3, 5, 7, 6, 4, 2];

/** Absolute box for a circle of `radius` centred on the flower. */
function ringBox(radius: number) {
  return {
    left: CENTER.x - radius,
    top: CENTER.y - radius,
    width: radius * 2,
    height: radius * 2,
  };
}

export function HomeHero() {
  const { t } = useI18n();
  const [storyOpen, setStoryOpen] = useState(false);

  return (
    // Pulled up by the header's height so the photo shows through the
    // see-through header; the same amount is added back as top padding.
    <section
      className="relative isolate overflow-hidden bg-background"
      style={{
        marginTop: "calc(var(--site-header-h, 0px) * -1)",
        paddingTop: "var(--site-header-h, 0px)",
      }}
    >
      <HeroBackdrop />

      <div className="relative z-10 mx-auto grid max-w-site items-center gap-10 px-4 pb-24 pt-10 sm:px-6 md:pb-28 md:pt-12 lg:grid-cols-[1fr_1fr] lg:gap-10 lg:px-8 xl:gap-16">
        {/* ---------- Left: copy ---------- */}
        <div>
          <div className="anim-fade-up" style={{ animationDelay: "150ms" }}>
            <span className="border-gold/50 bg-gold/15 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold tracking-wider text-[#B07A12]">
              <span className="text-base leading-none" aria-hidden>
                🙏
              </span>
              {t("home.hero.greeting")}
              <Sparkles className="h-3.5 w-3.5 text-gold" aria-hidden />
            </span>
          </div>

          <h1 className="mt-7 !leading-[1.1] tracking-tight">
            <span className="anim-line" style={{ animationDelay: "280ms" }}>
              <span className="text-foreground/80 block font-serif !text-[2rem] !font-normal sm:!text-[2.6rem] lg:!text-[2.3rem] xl:!text-[2.6rem] 2xl:!text-[3rem]">
                {t("home.hero.titleLine1")}
              </span>
            </span>
            <span
              className="anim-line mt-3"
              style={{ animationDelay: "440ms" }}
            >
              <span className="block font-sans !text-[2.6rem] !font-bold italic text-primary sm:!text-[3.4rem] xl:!text-[3.6rem]">
                {t("home.hero.titleLine2")}
              </span>
            </span>
          </h1>

          <div className="anim-fade-up" style={{ animationDelay: "640ms" }}>
            <div className="mt-5 flex items-center gap-3">
              <span
                className="anim-draw-x h-px w-10 bg-gold"
                style={{ animationDelay: "700ms" }}
                aria-hidden
              />
              <p className="font-serif !text-lg italic text-muted-foreground sm:!text-xl">
                {t("home.hero.sanskrit")}
              </p>
            </div>
          </div>

          <div className="anim-fade-up" style={{ animationDelay: "760ms" }}>
            <p className="mt-6 max-w-xl !text-base !leading-relaxed text-muted-foreground sm:!text-lg">
              {t("home.hero.description")}
            </p>
          </div>

          <div className="anim-fade-up" style={{ animationDelay: "880ms" }}>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="shadow-gold/30 hover:bg-gold/90 btn-lift group rounded-full bg-gold text-gold-foreground shadow-md hover:shadow-lg"
              >
                <Link to="/admission-inquiry">
                  <ClipboardEdit className="mr-2 h-4 w-4" />
                  {t("home.hero.admissionInquiry")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setStoryOpen(true)}
                className="border-primary/30 btn-lift group rounded-full bg-background text-primary hover:bg-primary hover:text-primary-foreground"
              >
                <Play className="mr-2 h-4 w-4 fill-current" />
                {t("home.hero.watchStory")}
              </Button>
            </div>
          </div>

          <div className="anim-fade-up" style={{ animationDelay: "1000ms" }}>
            <ul className="mt-10 flex max-w-2xl flex-wrap gap-x-6 gap-y-3 border-t border-border pt-6">
              {[
                t("home.hero.feature1"),
                t("home.hero.feature2"),
                t("home.hero.feature3"),
              ].map(f => (
                <li
                  key={f}
                  className="text-foreground/80 flex items-center gap-2 text-sm font-medium"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-gold" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ---------- Right: service flower ---------- */}
        <div className="w-full">
          <ServiceFlower />
        </div>
      </div>

      <Dialog open={storyOpen} onOpenChange={setStoryOpen}>
        <DialogContent className="max-w-4xl overflow-hidden border-0 bg-black p-0">
          <DialogTitle className="sr-only">
            {t("home.hero.watchStory")}
          </DialogTitle>
          {storyOpen && (
            <video
              className="aspect-video w-full bg-black"
              src={STORY_VIDEO_URL}
              controls
              autoPlay
              playsInline
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ServiceFlower() {
  return (
    <>
      <MobileServiceGrid />
      <DesktopFlower />
    </>
  );
}

/** Scales the fixed-size flower canvas to its container width. */
function useFitScale(baseWidth: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const update = () => setScale(el.clientWidth / baseWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [baseWidth]);

  return { ref, scale };
}

function DesktopFlower() {
  const { t } = useI18n();
  const { ref, scale } = useFitScale(FLOWER_W);

  return (
    <div
      ref={ref}
      className="relative mx-auto hidden w-full max-w-[600px] sm:block"
      style={{ aspectRatio: `${FLOWER_W} / ${FLOWER_H}` }}
    >
      <div
        className="anim-iris absolute left-0 top-0 origin-top-left"
        style={{
          width: FLOWER_W,
          height: FLOWER_H,
          transform: `scale(${scale})`,
        }}
      >
        {/* Guide ring behind the petals, with two lights circling it */}
        <div
          className="anim-fade pointer-events-none absolute rounded-full border-[1.5px] border-[#D9A066]/60"
          style={{ ...ringBox(RING_RADIUS), animationDelay: "500ms" }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute animate-[spin_16s_linear_infinite] motion-reduce:animate-none"
          style={ringBox(RING_RADIUS)}
          aria-hidden
        >
          <div
            className="anim-fade absolute inset-0"
            style={{ animationDelay: "1500ms" }}
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "conic-gradient(transparent 0deg 110deg, rgba(217,83,30,0.75) 180deg, transparent 180deg 290deg, rgba(31,95,174,0.6) 360deg)",
                WebkitMask:
                  "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
                mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
              }}
            />
            <span className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1F5FAE] shadow-[0_0_0_4px_rgba(31,95,174,0.18),0_0_14px_rgba(31,95,174,0.7)]" />
            <span className="absolute bottom-0 left-1/2 h-3 w-3 -translate-x-1/2 translate-y-1/2 rounded-full bg-[#D9531E] shadow-[0_0_0_4px_rgba(217,83,30,0.18),0_0_14px_rgba(217,83,30,0.7)]" />
          </div>
        </div>

        <ul>
          {services.map((s, i) => {
            const row = Math.floor(i / 2);
            const isLeft = s.side === "left";
            const path = petalPath(row, !isLeft);
            const l = LEFT_LAYOUT[row];
            const cx = isLeft ? l.cx : FLOWER_W - l.cx;

            return (
              <li
                key={s.titleKey}
                className="anim-petal-in pointer-events-none absolute inset-0 drop-shadow-[0_12px_14px_rgba(70,35,10,0.22)]"
                style={
                  {
                    animationDelay: `${650 + BLOOM_ORDER.indexOf(i) * 90}ms`,
                    "--petal-origin": `${CENTER.x}px ${CENTER.y}px`,
                  } as CSSProperties
                }
              >
                <Link
                  to={s.to}
                  className="group pointer-events-auto absolute inset-0 text-white transition-transform duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:brightness-110"
                  style={{ clipPath: `path("${path}")` }}
                >
                  <span
                    className="absolute inset-0"
                    style={{ backgroundImage: s.gradient }}
                    aria-hidden
                  />
                  {/* soft sheen */}
                  <span
                    className="absolute inset-0 bg-gradient-to-br from-white/[0.16] via-transparent to-black/[0.08]"
                    aria-hidden
                  />
                  {/* light rim along the outline */}
                  <svg
                    className="absolute inset-0"
                    width={FLOWER_W}
                    height={FLOWER_H}
                    aria-hidden
                  >
                    <path
                      d={path}
                      fill="none"
                      stroke="rgba(255,255,255,0.45)"
                      strokeWidth={3}
                    />
                  </svg>

                  <span
                    className="absolute grid place-items-center rounded-full bg-white shadow-[0_4px_10px_rgba(0,0,0,0.15)] transition-transform duration-300 group-hover:scale-105"
                    style={{
                      left: cx - l.iconSize / 2,
                      top: l.iconTop,
                      width: l.iconSize,
                      height: l.iconSize,
                    }}
                  >
                    <s.icon
                      className="h-[26px] w-[26px]"
                      style={{ color: s.accent }}
                      strokeWidth={2}
                    />
                  </span>

                  <span
                    className="absolute text-balance text-center text-[13.5px] font-medium leading-[1.2] [text-shadow:0_1px_2px_rgba(0,0,0,0.2)]"
                    style={{
                      left: cx - l.textW / 2,
                      top: l.iconTop + l.iconSize + ICON_TEXT_GAP,
                      width: l.textW,
                    }}
                  >
                    {t(s.titleKey)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Centre circle, framed by a slowly turning dashed ring */}
        <div
          className="anim-fade pointer-events-none absolute"
          style={{ ...ringBox(CENTER_SIZE / 2 + 12), animationDelay: "1200ms" }}
          aria-hidden
        >
          <div
            className="pointer-events-none absolute inset-0 animate-[spin_40s_linear_infinite_reverse] rounded-full border-2 border-dashed border-[#D9A066]/70 motion-reduce:animate-none"
            aria-hidden
          />
        </div>
        <div
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: CENTER.x, top: CENTER.y }}
        >
          <div className="anim-zoom-in" style={{ animationDelay: "450ms" }}>
            <CenterBadge size={CENTER_SIZE} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Phones: centre badge on top, petals as a simple two-column grid. */
function MobileServiceGrid() {
  const { t } = useI18n();

  return (
    <div className="sm:hidden">
      <Reveal variant="zoom" className="mb-4 flex justify-center">
        <CenterBadge size={148} />
      </Reveal>
      <Stagger as="ul" className="grid grid-cols-2 gap-3" step={90}>
        {services.map((s, i) => (
          <StaggerItem as="li" key={s.titleKey} index={i} from="zoom">
            <Link
              to={s.to}
              style={{ backgroundImage: s.gradient }}
              className="flex h-full min-h-[118px] flex-col items-center justify-center gap-3 rounded-2xl p-4 text-center text-white shadow-[0_14px_30px_-14px_rgba(60,30,10,0.45)]"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-white shadow-md">
                <s.icon
                  className="h-5 w-5"
                  style={{ color: s.accent }}
                  strokeWidth={2}
                />
              </span>
              <span className="text-[13px] font-semibold leading-snug">
                {t(s.titleKey)}
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  );
}

function CenterBadge({ size }: { size: number }) {
  const { t } = useI18n();
  return (
    <div
      className="grid place-items-center rounded-full bg-[#FBF6EE] p-[9px] shadow-[0_16px_36px_-14px_rgba(70,35,10,0.35)]"
      style={{ width: size, height: size }}
    >
      <div className="grid h-full w-full place-items-center rounded-full bg-white p-[6px] shadow-[inset_0_0_0_1px_#EEE3D3]">
        <img
          src={logo}
          alt={t("brand.title")}
          width={640}
          height={640}
          decoding="async"
          className="h-full w-full rounded-full object-cover"
        />
      </div>
    </div>
  );
}

/** Stacked tiffin carrier with a carry handle. */
function TiffinIcon({ className = "", style, strokeWidth = 2 }: LucideProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden
    >
      <path d="M9 5V3h6v2" />
      <rect x="6" y="5" width="12" height="5" rx="1.5" />
      <rect x="6" y="10" width="12" height="5" rx="1.5" />
      <rect x="6" y="15" width="12" height="5" rx="1.5" />
      <path d="M4.5 7.5v10M19.5 7.5v10" />
    </svg>
  );
}

/**
 * Sunlit vrudhashram courtyard photo behind the hero. A warm cream wash keeps
 * the copy readable on the left and lets the photo show through on the right,
 * with a soft veil behind the flower so the petals stay crisp.
 */
function HeroBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-0" aria-hidden>
      <img
        src={heroBg}
        alt=""
        width={1672}
        height={941}
        decoding="async"
        className="anim-bg-settle absolute inset-0 !h-full !w-full !max-w-none object-cover object-[center_40%]"
      />

      {/* readability wash: even on phones; on desktop strong behind the copy,
          fading out towards the right */}
      <div className="absolute inset-0 bg-[#FBF6EE]/[0.88] lg:bg-transparent lg:bg-[linear-gradient(90deg,rgba(251,246,238,0.96)_0%,rgba(251,246,238,0.9)_38%,rgba(251,246,238,0.55)_52%,rgba(251,246,238,0)_72%)]" />
      {/* light black tint on the photo, fading in from behind the copy */}
      <div className="absolute inset-0 hidden bg-[linear-gradient(90deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0)_42%,rgba(0,0,0,0.22)_62%,rgba(0,0,0,0.38)_100%)] lg:block" />
      {/* blend into the page at the bottom */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-background" />
    </div>
  );
}
